/**
 * Blademaster: melee DPS (PLAN §4). Arms shape, reworked for rhythm (docs/design/classes/blademaster.md).
 *
 * Loop: Overpower, Slam, Cleave and Colossus Smash carve **Wounds** (a per-target debuff, max 5)
 * into enemies; Mortal Strike, Execute and Ravager's Finale cash them in for ×(1 + 0.1 per stack).
 * Between those beats two visible procs change the next press:
 *  - **Tactician** (Mortal Strike / Cleave / Execute / Slam / Rend, doubled in Colossus Smash):
 *    +1 Overpower charge, next Overpower +50% and +2 Wounds.
 *  - **Sudden Death** (landed autos, Slam, Rend's bleed; 2 stacks): Execute is free and usable at any HP.
 * Colossus Smash (30 s) is the short window, Bladestorm (60 s) the major one: its end sets 5 Wounds on
 * everything it hit and Q transforms into **Ravager's Finale**, which detonates them.
 *
 * Every random proc is a ProcSpec on a hidden passive with bad-luck protection (step = ⅓ of the
 * base chance), one ProcSpec per trigger source so each source has its own pity counter.
 */
import type { AbilityDef, AreaSpec, AuraDef, ClassDef, Condition, EffectSpec, ProcSpec } from '@keystone/sim';
import { ABILITY, AURA, CLASS, PASSIVE } from '../ids';
import { PLAYER_HP, baseStats } from '../balance';
import { OFF_GCD, aroundSelf, aroundTarget, dodge, interrupt, selfBuff } from './common';
import { BLADEMASTER_UNLOCKS } from './unlocks';

const A = ABILITY.blademaster;

// ---------------------------------------------------------------------------
// Shared auras
// ---------------------------------------------------------------------------

/** Per-stack payoff of Wounds on the unit being hit (F7). */
const WOUNDS_PER_STACK = { auraId: AURA.wounds, on: 'target', mult: 0.1 } as const;

/** Wounds: per-caster debuff stack, cashed in by Mortal Strike, Execute and Ravager's Finale. */
export const WOUNDS: AuraDef = {
  id: AURA.wounds,
  name: 'Wounds',
  kind: 'debuff',
  durationMs: 15000,
  stacking: 'stack',
  maxStacks: 5,
  ui: { stackCounterOn: [A.mortalStrike], glow: { abilityIds: [A.mortalStrike], atStacks: 4 } },
};

/** Tactician (existing id): +1 Overpower charge, next Overpower +50% and +2 Wounds. */
export const TACTICIAN: AuraDef = {
  id: AURA.overpowerProc,
  name: 'Tactician',
  kind: 'buff',
  durationMs: 10000,
  stacking: 'refresh',
  mods: [{ stat: 'damageDonePct', value: 0.5, abilityIds: [A.overpower] }],
  procs: [{ on: 'castResolved', abilityIds: [A.overpower], consumeStack: true, effects: [] }],
  ui: { proc: true },
};

/** Sudden Death: Execute costs no Rage and ignores the health gate; one stack per Execute. */
export const SUDDEN_DEATH: AuraDef = {
  id: AURA.suddenDeath,
  name: 'Sudden Death',
  kind: 'buff',
  durationMs: 12000,
  stacking: 'stack',
  maxStacks: 2,
  mods: [{ stat: 'resourceCostPct', value: -1, abilityIds: [A.execute], ignoreStacks: true }],
  procs: [{ on: 'castResolved', abilityIds: [A.execute], consumeStack: true, effects: [] }],
  ui: { proc: true, stackCounterOn: [A.execute] },
};

/** Sweeping Edge (existing id): the next Mortal Strike cleaves. */
export const SWEEPING_EDGE: AuraDef = {
  id: AURA.cleaveBuff,
  name: 'Sweeping Edge',
  kind: 'buff',
  durationMs: 10000,
  stacking: 'refresh',
  mods: [],
  ui: { glow: { abilityIds: [A.mortalStrike] } },
};

/** Ravager's Finale (transform offer): Q casts Ravager's Finale instead of Bladestorm. */
export const RAVAGERS_FINALE_READY: AuraDef = {
  id: AURA.ravagersFinaleReady,
  name: "Ravager's Finale",
  kind: 'buff',
  durationMs: 10000,
  stacking: 'refresh',
  overrides: [{ abilityId: A.bladestorm, with: A.ravagersFinale }],
  ui: { proc: true },
};

/** Rend: 18 s bleed, 0.6 every 3 s (haste-scaled, snapshot), pandemic, partial final tick. */
export const REND_BLEED: AuraDef = {
  id: AURA.rend,
  name: 'Rend',
  kind: 'debuff',
  durationMs: 18000,
  stacking: 'refresh',
  pandemic: true,
  periodic: { intervalMs: 3000, hasteScaled: true, partialFinalTick: true, effects: [{ type: 'damage', school: 'physical', amount: { coef: 0.6 } }] },
};

/** Add `n` Wounds to the primary target (or every unit of `area`). */
export function addWounds(n: number, area?: AreaSpec): EffectSpec {
  return { type: 'applyAura', aura: WOUNDS, ...(n > 1 ? { stacks: n } : {}), ...(area ? { to: 'area' as const, area } : {}) };
}

/** Consume the caster's Wounds on the primary target (or every unit of `area`). */
function consumeWounds(area?: AreaSpec): EffectSpec {
  return { type: 'removeAura', auraId: AURA.wounds, ...(area ? { to: 'area' as const, area } : {}) };
}

// ---------------------------------------------------------------------------
// Abilities
// ---------------------------------------------------------------------------

const SWEEP_AREA: AreaSpec = { ...aroundTarget(8, 4), excludePrimary: true };

const mortalStrike: AbilityDef = {
  id: A.mortalStrike,
  name: 'Mortal Strike',
  school: 'physical',
  description:
    'Main spender. Deals 10% more per Wounds stack on the target and consumes them. With Sweeping Edge, also cleaves up to 4 nearby enemies (each cashing its own Wounds). 20% chance to grant Tactician.',
  targeting: 'enemy',
  melee: true,
  requiresFacing: true,
  cooldown: { durationMs: 6000 },
  cost: [{ resource: 'rage', amount: 30 }],
  effects: [
    {
      type: 'conditional',
      cond: { type: 'selfHasAura', auraId: AURA.cleaveBuff },
      then: [
        { type: 'damage', school: 'physical', amount: { coef: 6.0, variance: 0.05, perStack: WOUNDS_PER_STACK } },
        consumeWounds(),
        { type: 'damage', to: 'area', area: SWEEP_AREA, school: 'physical', amount: { coef: 3.5, variance: 0.05, perStack: WOUNDS_PER_STACK }, isAoE: true },
        consumeWounds(SWEEP_AREA),
        { type: 'removeAura', to: 'self', auraId: AURA.cleaveBuff, stacks: 1 },
      ],
      else: [{ type: 'damage', school: 'physical', amount: { coef: 6.0, variance: 0.05, perStack: WOUNDS_PER_STACK } }, consumeWounds()],
    },
  ],
  ui: { tags: ['spender'], color: '#c0392b' },
};

const overpower: AbilityDef = {
  id: A.overpower,
  name: 'Overpower',
  school: 'physical',
  description: 'Builder with 2 charges. Generates 12 Rage and applies 1 Wounds. Tactician grants a charge and makes the next Overpower deal 50% more and apply 2 Wounds.',
  targeting: 'enemy',
  melee: true,
  requiresFacing: true,
  cooldown: { durationMs: 10000, charges: 2 },
  gain: [{ resource: 'rage', amount: 12 }],
  effects: [
    { type: 'damage', school: 'physical', amount: { coef: 3.3, variance: 0.05 } },
    { type: 'conditional', cond: { type: 'selfHasAura', auraId: AURA.overpowerProc }, then: [addWounds(2)], else: [addWounds(1)] },
  ],
  ui: { tags: ['builder'], color: '#e67e22' },
};

const CLEAVE_AREA = aroundTarget(8, 5);

const cleave: AbilityDef = {
  id: A.cleave,
  name: 'Cleave',
  school: 'physical',
  description: 'Hit up to 5 enemies near the target and apply 1 Wounds to each. Your next Mortal Strike cleaves (Sweeping Edge). 20% chance to grant Tactician.',
  targeting: 'enemy',
  melee: true,
  requiresFacing: true,
  cost: [{ resource: 'rage', amount: 30 }],
  effects: [
    { type: 'damage', to: 'area', area: CLEAVE_AREA, school: 'physical', amount: { coef: 2.4, variance: 0.05 } },
    addWounds(1, CLEAVE_AREA),
    { type: 'applyAura', to: 'self', aura: SWEEPING_EDGE },
  ],
  ui: { tags: ['aoe'], color: '#c0392b' },
};

const execute: AbilityDef = {
  id: A.execute,
  name: 'Execute',
  school: 'physical',
  description:
    'Finisher, usable on targets below 20% health or with Sudden Death (which also makes it free). Deals 10% more per Wounds stack and consumes them. 10% chance to grant Tactician.',
  targeting: 'enemy',
  melee: true,
  requiresFacing: true,
  cost: [{ resource: 'rage', amount: 20 }],
  usableIf: { type: 'or', any: [{ type: 'targetHpBelow', pct: 0.2 }, { type: 'selfHasAura', auraId: AURA.suddenDeath }] },
  effects: [{ type: 'damage', school: 'physical', amount: { coef: 5.0, variance: 0.05, perStack: WOUNDS_PER_STACK } }, consumeWounds()],
  ui: { tags: ['execute', 'spender'], color: '#8e1b10' },
};

const slam: AbilityDef = {
  id: A.slam,
  name: 'Slam',
  school: 'physical',
  description: 'Cheap filler. Applies 1 Wounds. 20% chance to grant Sudden Death and 15% chance to grant Tactician.',
  targeting: 'enemy',
  melee: true,
  requiresFacing: true,
  cost: [{ resource: 'rage', amount: 10 }],
  effects: [{ type: 'damage', school: 'physical', amount: { coef: 2.6, variance: 0.05 } }, addWounds(1)],
  ui: { tags: ['builder'], color: '#a04000' },
};

const rend: AbilityDef = {
  id: A.rend,
  name: 'Rend',
  school: 'physical',
  description: 'Wound the target and make it bleed for 18 s (refresh in the last 30%). The hit and each bleed tick have a 6% chance to grant Sudden Death. 10% chance to grant Tactician.',
  targeting: 'enemy',
  melee: true,
  requiresFacing: true,
  cost: [{ resource: 'rage', amount: 10 }],
  effects: [{ type: 'damage', school: 'physical', amount: { coef: 0.8, variance: 0.05 } }, { type: 'applyAura', aura: REND_BLEED }],
  ui: { tags: ['builder'], color: '#7b241c' },
};

const BLADESTORM_AREA = aroundSelf(8, 8);

const bladestorm: AbilityDef = {
  id: A.bladestorm,
  name: 'Bladestorm',
  school: 'physical',
  description:
    "Spin for 8 s hitting up to 8 nearby enemies every second. Immune to control. When the spin ends, every enemy around you is set to 5 Wounds and Bladestorm becomes Ravager's Finale for 10 s.",
  targeting: 'none',
  cast: { type: 'channel', durationMs: 8000, ticks: 8 },
  canCastWhileMoving: true,
  cooldown: { durationMs: 60000 },
  channelStartEffects: [selfBuff(AURA.bladestorm, 'Bladestorm', 8000, [], { flags: { immuneCc: true } })],
  effects: [{ type: 'damage', to: 'area', area: BLADESTORM_AREA, school: 'physical', amount: { coef: 2.3, variance: 0.05 } }],
  channelEndEffects: [addWounds(5, BLADESTORM_AREA), { type: 'applyAura', to: 'self', aura: RAVAGERS_FINALE_READY }],
  channelEndOn: 'any',
  ui: { tags: ['aoe', 'cooldown'], color: '#c0392b' },
};

const FINALE_AREA: AreaSpec = { ...aroundTarget(8, 8), excludePrimary: true };

/** Transform form of Bladestorm (offered by Ravager's Finale). Unbound: reached through the override. */
const ravagersFinale: AbilityDef = {
  id: A.ravagersFinale,
  name: "Ravager's Finale",
  school: 'physical',
  description: 'Slam the target and up to 8 enemies near it, dealing 10% more per Wounds stack on each and consuming them.',
  targeting: 'enemy',
  melee: true,
  requiresFacing: true,
  effects: [
    { type: 'damage', school: 'physical', amount: { coef: 6.0, variance: 0.05, perStack: WOUNDS_PER_STACK } },
    consumeWounds(),
    { type: 'damage', to: 'area', area: FINALE_AREA, school: 'physical', amount: { coef: 3.0, variance: 0.05, perStack: WOUNDS_PER_STACK }, isAoE: true },
    consumeWounds(FINALE_AREA),
    { type: 'removeAura', to: 'self', auraId: AURA.ravagersFinaleReady },
  ],
  ui: { tags: ['aoe', 'spender'], color: '#8e1b10', animAs: A.mortalStrike },
};

const colossusSmash: AbilityDef = {
  id: A.colossusSmash,
  name: 'Colossus Smash',
  school: 'physical',
  description: 'Smash the target, apply 2 Wounds and gain an Overpower charge. For 8 s you deal 20% more damage and your Tactician chances are doubled.',
  targeting: 'enemy',
  melee: true,
  requiresFacing: true,
  cooldown: { durationMs: 30000 },
  effects: [
    { type: 'damage', school: 'physical', amount: { coef: 3.0, variance: 0.05 } },
    addWounds(2),
    { type: 'modifyCooldown', to: 'self', abilityId: A.overpower, addCharge: true },
    selfBuff(AURA.colossusSmash, 'Colossus Smash', 8000, [{ stat: 'damageDonePct', value: 0.2 }]),
  ],
  ui: { tags: ['cooldown'], color: '#8e44ad' },
};

const pummel: AbilityDef = {
  id: A.pummel,
  name: 'Pummel',
  school: 'physical',
  description: 'Interrupt the target and lock its school for 4 s.',
  targeting: 'enemy',
  melee: true,
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 15000 },
  effects: [interrupt(4000)],
  ui: { tags: ['interrupt'], color: '#ffffff' },
};

const dieByTheSword: AbilityDef = {
  id: A.dieByTheSword,
  name: 'Die by the Sword',
  school: 'physical',
  description: 'Reduce damage taken by 30% and parry all frontal melee for 8 s.',
  targeting: 'self',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 60000 },
  effects: [
    selfBuff(AURA.dieByTheSword, 'Die by the Sword', 8000, [
      { stat: 'damageTakenPct', value: -0.3 },
      { stat: 'parry', value: 1 },
    ]),
  ],
  ui: { tags: ['defensive'], color: '#bdc3c7' },
};

const charge: AbilityDef = {
  id: A.charge,
  name: 'Charge',
  school: 'physical',
  description: 'Charge to an enemy. Generates 15 Rage.',
  targeting: 'enemy',
  range: 25,
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 20000 },
  gain: [{ resource: 'rage', amount: 15 }],
  effects: [{ type: 'dash', mode: 'toTarget', distance: 25, speed: 30 }],
  ui: { tags: ['movement'], color: '#bdc3c7' },
};

// ---------------------------------------------------------------------------
// Procs (hidden passives; one ProcSpec per trigger source = one pity counter each)
// ---------------------------------------------------------------------------

const IN_SMASH: Condition = { type: 'selfHasAura', auraId: AURA.colossusSmash };
const NOT_IN_SMASH: Condition = { type: 'selfLacksAura', auraId: AURA.colossusSmash };

/** Bad-luck step: ⅓ of the base chance per miss. */
const blp = (chance: number): ProcSpec['badLuck'] => ({ step: chance / 3 });

const grantTactician: EffectSpec[] = [
  { type: 'modifyCooldown', to: 'self', abilityId: A.overpower, addCharge: true },
  { type: 'applyAura', to: 'self', aura: TACTICIAN },
];

/** Tactician chance per rotational ability (Colossus Smash doubles it). */
export const TACTICIAN_CHANCES: ReadonlyArray<readonly [string, number]> = [
  [A.mortalStrike, 0.2],
  [A.cleave, 0.2],
  [A.execute, 0.1],
  [A.slam, 0.15],
  [A.rend, 0.1],
];

const tacticianProcs: ProcSpec[] = TACTICIAN_CHANCES.flatMap(([abilityId, chance]): ProcSpec[] => [
  { on: 'castResolved', abilityIds: [abilityId], chance, badLuck: blp(chance), if: NOT_IN_SMASH, effects: grantTactician },
  { on: 'castResolved', abilityIds: [abilityId], chance: chance * 2, badLuck: blp(chance * 2), if: IN_SMASH, effects: grantTactician },
]);

const grantSuddenDeath: EffectSpec[] = [{ type: 'applyAura', to: 'self', aura: SUDDEN_DEATH }];

const suddenDeathProcs: ProcSpec[] = [
  { on: 'meleeSwingLanded', chance: 0.06, badLuck: blp(0.06), effects: grantSuddenDeath },
  { on: 'castResolved', abilityIds: [A.slam], chance: 0.2, badLuck: blp(0.2), effects: grantSuddenDeath },
  // Rend's bleed ticks carry the aura id, which equals the ability id: the initial hit rolls too.
  { on: 'damageDealt', abilityIds: [AURA.rend], chance: 0.06, badLuck: blp(0.06), effects: grantSuddenDeath },
];

export const blademaster: ClassDef = {
  id: CLASS.blademaster,
  name: 'Blademaster',
  role: 'dps',
  resources: [{ id: 'rage', name: 'Rage', max: 100, start: 0, regenPerSecOoc: -5, gainOn: [{ on: 'meleeSwingLanded', amount: 10 }] }],
  binds: {
    '1': A.mortalStrike,
    '2': A.overpower,
    '3': A.cleave,
    '4': A.execute,
    Q: A.bladestorm,
    E: A.colossusSmash,
    R: A.pummel,
    F: A.dieByTheSword,
  },
  extraBinds: { '5': A.slam, '6': A.rend, G: A.charge },
  abilities: [mortalStrike, overpower, cleave, execute, slam, rend, bladestorm, ravagersFinale, colossusSmash, pummel, dieByTheSword, charge, dodge],
  // Charge stays first (the web client's G fallback reads extraAbilities[0]); Ravager's Finale is
  // the unbound transform form of Bladestorm.
  extraAbilities: [A.charge, A.ravagersFinale, ABILITY.common.dodge],
  baseStats: baseStats({ armor: 0.2, dodge: 0.03, parry: 0.05 }),
  maxHp: PLAYER_HP.melee,
  moveSpeed: 7,
  melee: { damage: { coef: 2.0, variance: 0.05 }, speedMs: 3000, school: 'physical' },
  passives: [
    {
      id: PASSIVE.bladeTactician,
      name: 'Tactician',
      kind: 'buff',
      durationMs: 0,
      stacking: 'unique',
      flags: { hidden: true, persistThroughDeath: true },
      procs: tacticianProcs,
    },
    {
      id: PASSIVE.bladeSuddenDeath,
      name: 'Sudden Death',
      kind: 'buff',
      durationMs: 0,
      stacking: 'unique',
      flags: { hidden: true, persistThroughDeath: true },
      procs: suddenDeathProcs,
    },
  ],
  color: '#c0392b',
  // +field: hero-level ability gates (unlocks.ts).
  unlocks: BLADEMASTER_UNLOCKS,
};
